'use client';

import { useSearchParams } from 'next/navigation';
import { tenant } from '@/interfaces/common';
import { setCookie } from 'cookies-next';

import useMinifyChecker from '@/hooks/useMinifyChecker';

import LeftSidebar from './sections/LeftSidebar';
import LoginForm from './sections/LoginForm';
import SSOButton from './sections/SSOButton';

export default function LoginPage({
  tenant,
  clientId,
  sso,
  ssoLink,
  encodedParams,
  shouldShowContinueWithSSOButton,
}: {
  tenant: tenant;
  clientId: string;
  sso: boolean;
  ssoLink: string;
  encodedParams: any;
  shouldShowContinueWithSSOButton: boolean;
}) {
  if (
    typeof window !== 'undefined' &&
    tenant &&
    Object.keys(tenant)?.length > 0 &&
    tenant !== undefined
  ) {
    window.sessionStorage.setItem('tenant', JSON.stringify(tenant));
    window.sessionStorage.setItem('ssoLink', ssoLink);
    setCookie('tenant', JSON.stringify(tenant));

    const oneHourFromNow = new Date();
    oneHourFromNow.setHours(oneHourFromNow.getHours() + 1);
    setCookie('encodedParams', JSON.stringify(encodedParams), {
      expires: oneHourFromNow,
    });
  }

  const searchParams = useSearchParams();
  const minifyParams = searchParams.get('minify') as string | undefined;
  const postParent = searchParams.get('postParent') as string | undefined;
  const app_id = searchParams.get('app_id') as string | undefined;
  // Standardize on redirect_uri (OAuth/OIDC standard) but support redirecturl for backward compatibility
  const redirectURLParams = (searchParams.get('redirect_uri') || searchParams.get('redirecturl')) as
    | string
    | undefined;
  const locale = searchParams.get('locale') as string | null;

  if (redirectURLParams) {
    if (locale) {
      setCookie('hasLocaleParam', true);
    } else {
      setCookie('hasLocaleParam', false);
    }
  }

  if (
    typeof window !== 'undefined' &&
    redirectURLParams &&
    redirectURLParams !== undefined
  ) {
    // Store as redirect_uri (standard) but also keep redirecturl for backward compatibility
    window.sessionStorage.setItem('redirect_uri', redirectURLParams);
    window.sessionStorage.setItem('redirecturl', redirectURLParams);
  }

  if (typeof window !== 'undefined' && postParent && postParent !== undefined) {
    window.sessionStorage.setItem('postParent', 'true');
  }

  if (app_id && typeof window !== 'undefined') {
    window.sessionStorage.setItem('app_id', app_id);
    setCookie('app_id', app_id);
  } else if (typeof window !== 'undefined') {
    window.sessionStorage.removeItem('app_id');
    setCookie('app_id', '');
  }

  const { setMinifyInSessionStorage } = useMinifyChecker();
  setMinifyInSessionStorage(minifyParams);

  const { minify } = useMinifyChecker();

  // DEBUG: Log tenant information (can be removed after testing)
  if (typeof window !== 'undefined') {
    console.log('🔍 Tenant Debug Info:', {
      tenantName: tenant?.tenantName,
      sso,
      ssoLink,
      tenantId: tenant?._id
    });
  }

  return (
    <div className="h-screen bg-zinc-100 p-[24px]">
      {sso ? (
        // All SSO tenants now use SSOButton with correct link
        <SSOButton ssoLink={ssoLink} />
      ) : (
        <div
          className={`rounded-3xl bg-white ${minify ? 'grid-cols-1' : 'grid-cols-2'} grid`}
        >
          <LoginForm
            shouldShowContinueWithSSOButton={shouldShowContinueWithSSOButton}
            ssoLink={ssoLink}
            tenant={tenant}
            clientId={clientId}
            app_id={app_id}
          />
          {!minify && <LeftSidebar tenant={tenant} />}
        </div>
      )}
    </div>
  );
}
